// Error codes to messages (FR-013 to FR-015, FR-022, FR-026, FR-044; research R11). Screens pick by
// AppError.code first, then by kind. No message reveals whether an email exists (SC-005).
import type { AppError } from "@/api/errors";
import type { Api } from "@/api/types";

import { AUTH_COPY } from "./copy";
import type { FieldCode } from "./validation";

export type AuthOperation =
  | "signInWithEmail"
  | "registerWithEmail"
  | "verifyEmail"
  | "resendVerificationEmail"
  | "acceptInvite"
  | "requestPasswordReset"
  | "resetPassword"
  | "refreshSession"
  | "signOut";

export type AuthField =
  "name" | "email" | "password" | "confirmPassword" | "mobile";

/** What a screen shows for a failed call. */
export type AuthErrorView =
  /** A message above the form (with a support reference for server errors). */
  | { kind: "banner"; text: string; reference: string | null }
  /** FR-014: "Confirm your email first" with Resend email. */
  | { kind: "notVerified"; text: string }
  /** FR-016: retry message; the form keeps its values. */
  | { kind: "connection"; text: string }
  /** FR-011, FR-040, FR-045: the page's "link no longer works" state. */
  | { kind: "linkInvalid" }
  /** FR-005: messages on the matching fields. */
  | { kind: "fields"; fields: Partial<Record<AuthField, string>> };

const E = AUTH_COPY.errors;
const F = AUTH_COPY.fieldErrors;

const LINK_OPERATIONS: readonly AuthOperation[] = [
  "verifyEmail",
  "resetPassword",
  "acceptInvite",
];

/** FR-026: admin before driver when both appear; neither → "doesn't have access". */
export function otherClientMessage(
  allowedClients: readonly Api["Client"][] | null,
): string {
  if (allowedClients?.includes("admin")) return AUTH_COPY.refusals.admin;
  if (allowedClients?.includes("driver")) return AUTH_COPY.refusals.driver;
  return AUTH_COPY.refusals.noAccess;
}

/** "1 min 30 s", "2 min", "45 s". */
export function waitText(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s} s`;
  return s === 0 ? `${m} min` : `${m} min ${s} s`;
}

export function rateLimitedMessage(retryAfterSeconds: number | null): string {
  return retryAfterSeconds !== null && retryAfterSeconds > 0
    ? E.rateLimitedIn(waitText(retryAfterSeconds))
    : E.rateLimited;
}

/** The message for a field code, from the client checks or from the server's field errors. */
export function fieldMessage(
  field: AuthField,
  code: FieldCode | string,
): string {
  switch (code) {
    case "REQUIRED":
      return F.required[field];
    case "INVALID_FORMAT":
      return F.invalidFormat[field];
    case "TOO_SHORT":
      return field === "password" || field === "confirmPassword"
        ? F.tooShort
        : F.unknown;
    case "TOO_LONG":
      if (field === "name") return F.nameTooLong;
      if (field === "email") return F.emailTooLong;
      return field === "password" || field === "confirmPassword"
        ? F.tooLong
        : F.unknown;
    case "PASSWORD_TOO_COMMON":
      return F.tooCommon;
    case "PASSWORD_MATCHES_EMAIL":
      return F.matchesEmail;
    case "MISMATCH":
      return F.mismatch;
    default:
      return F.unknown;
  }
}

/** The server's field names (`newPassword` → the password field); others are not on any form. */
const SERVER_FIELDS: Readonly<Record<string, AuthField>> = {
  name: "name",
  email: "email",
  password: "password",
  newPassword: "password",
  mobile: "mobile",
};

function fieldsFrom(error: AppError): Partial<Record<AuthField, string>> {
  const fields: Partial<Record<AuthField, string>> = {};
  for (const fe of error.fieldErrors) {
    const field = SERVER_FIELDS[fe.field];
    if (field && fields[field] === undefined) {
      fields[field] = fieldMessage(field, fe.code);
    }
  }
  return fields;
}

const generic = (error: AppError): AuthErrorView => ({
  kind: "banner",
  text: E.generic,
  reference: error.requestId,
});

const banner = (text: string): AuthErrorView => ({
  kind: "banner",
  text,
  reference: null,
});

export function messageFor(
  error: AppError,
  operation: AuthOperation,
): AuthErrorView {
  switch (error.code) {
    case "INVALID_CREDENTIALS":
      return operation === "signInWithEmail"
        ? banner(E.invalidCredentials)
        : generic(error);
    case "EMAIL_NOT_VERIFIED":
      return { kind: "notVerified", text: E.notVerified };
    case "NOT_PERMITTED_FOR_CLIENT":
      return banner(otherClientMessage(error.allowedClients));
    case "ACCOUNT_DISABLED":
      return banner(AUTH_COPY.refusals.noAccess);
    case "SESSION_ENDED":
      return banner(E.sessionEnded);
    case "TOKEN_INVALID_OR_EXPIRED":
      return LINK_OPERATIONS.includes(operation)
        ? { kind: "linkInvalid" }
        : generic(error);
    default:
      break;
  }
  switch (error.kind) {
    case "rateLimited":
      return banner(rateLimitedMessage(error.retryAfterSeconds));
    case "offline":
    case "timeout":
      return { kind: "connection", text: E.connection };
    case "validation": {
      const fields = fieldsFrom(error);
      return Object.keys(fields).length > 0
        ? { kind: "fields", fields }
        : generic(error);
    }
    default:
      return generic(error);
  }
}
